import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NO_LAYOUT, type TableLayout } from '@/domain';
import type { SavedTableLayout } from '../ports/PreferencesRepository';
import { usePreferencesRepository } from '../container';
import { keys } from '../query-keys';

/**
 * How the person signed in reads one table, kept on their account. The request
 * of 7 October 2026, section 3.
 *
 * One request for every table they laid out, shared by every table on screen
 * and fresh for the session: a layout changes when its owner changes it, here,
 * and the cache is written the moment they do. So the table redraws at once and
 * the server is told behind it; if the server refuses, the list is read again
 * and the table goes back to what is really kept.
 */
export interface TableLayoutControl {
  tableKey: string;
  /** The layout kept for this table, or null for the table as it comes. */
  layout: TableLayout | null;
  /** The account has answered: until it has, `layout` is null because nothing is known yet. */
  loaded: boolean;
  save: (layout: TableLayout) => void;
  /** Back to the table as it comes. */
  reset: () => void;
}

export function useTableLayouts() {
  const repo = usePreferencesRepository();
  return useQuery({
    queryKey: keys.tableLayouts,
    queryFn: () => repo.tableLayouts(),
    staleTime: Infinity,
  });
}

export function useTableLayout(tableKey: string): TableLayoutControl {
  const repo = usePreferencesRepository();
  const queryClient = useQueryClient();
  const { data, isSuccess } = useTableLayouts();

  const saving = useMutation({
    mutationFn: (layout: TableLayout) => repo.saveTableLayout(tableKey, layout),
    onError: () => queryClient.invalidateQueries({ queryKey: keys.tableLayouts }),
  });
  const resetting = useMutation({
    mutationFn: () => repo.resetTableLayout(tableKey),
    onError: () => queryClient.invalidateQueries({ queryKey: keys.tableLayouts }),
  });

  const kept = data?.find((each) => each.table === tableKey) ?? null;

  const { mutate: send } = saving;
  const save = useCallback(
    (layout: TableLayout) => {
      queryClient.setQueryData<SavedTableLayout[]>(keys.tableLayouts, (current = []) => [
        ...current.filter((each) => each.table !== tableKey),
        { table: tableKey, ...layout },
      ]);
      send(layout);
    },
    [queryClient, send, tableKey],
  );

  const { mutate: sendReset } = resetting;
  const reset = useCallback(() => {
    queryClient.setQueryData<SavedTableLayout[]>(keys.tableLayouts, (current = []) =>
      current.filter((each) => each.table !== tableKey),
    );
    sendReset();
  }, [queryClient, sendReset, tableKey]);

  return useMemo(
    () => ({
      tableKey,
      layout: kept === null ? null : { order: kept.order, hidden: kept.hidden, pageSize: kept.pageSize },
      loaded: isSuccess,
      save,
      reset,
    }),
    [tableKey, kept, isSuccess, save, reset],
  );
}

/** The layout a control holds, or the table as it comes. */
export function layoutOrNone(control: TableLayoutControl): TableLayout {
  return control.layout ?? NO_LAYOUT;
}
